"use client"

import { forwardRef, type ForwardedRef } from "react"
import clsx from "clsx"

import type { Nullish, PropsWithClassName } from "~/@types/generics"
import { ProductCardSkeleton } from "~/components/ui/ProductCardRevamped/CardContent/skeleton.client"
import { ProductCardRevamped, type ProductCardRevampedProps } from "~/components/ui/ProductCardRevamped/index.client"
import { EmblaSlider } from "~/components/shared/EmblaSlider/index.client"
import type { Dictionary } from "~/providers/I18nProvider/constants"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import { sprinkles } from "~/styles/sprinkles.css"

import * as css from "./styles.css"

export const ProductsSlider = forwardRef(function WithRef(
  {
    className,
    isLoading,
    title,
    products,
  }: PropsWithClassName & {
    isLoading?: boolean
    title: keyof Dictionary
    products: Nullish<ProductCardRevampedProps[]>
  },
  ref: ForwardedRef<HTMLDivElement>
) {
  const t = useTranslate()

  if ((!products || !products?.length) && !isLoading) {
    return null
  }

  if (isLoading) {
    return (
      <div ref={ref} className={clsx(className, css.ProductsSlider)}>
        <header className={clsx(css.header)}>{title && <p className={css.headerTitle}>{t(title)}</p>}</header>
        <div className={clsx(css.slides, sprinkles({ display: "flex" }))}>
          {Array.from({ length: 4 })?.map((_, index) => {
            return <ProductCardSkeleton key={`products-slider-skeleton-${index}`} className={css.item} />
          })}
        </div>
      </div>
    )
  }

  return (
    <div ref={ref} className={clsx(className, css.ProductsSlider)}>
      <header className={clsx(css.header)}>{title && <p className={css.headerTitle}>{t(title)}</p>}</header>
      <EmblaSlider
        className={clsx(css.slider)}
        sliderClassName={css.slides}
        controlsClassName={css.navigation}
        buttonStyle="bordered"
        buttonType="arrow"
        hideOverflow
        controlsStyle="top-right"
        options={{
          align: "start",
          active: (products ?? [])?.length > 2,
          watchDrag: (products ?? [])?.length > 2,
        }}
      >
        {products?.map((item, index) => {
          return (
            <ProductCardRevamped
              className={css.item}
              key={`products-slider-${index}-${item?.product?.id}`}
              withSiblings
              trackAsAlgoliaConversion
              hasDrag={false}
              {...item}
            />
          )
        })}
      </EmblaSlider>
    </div>
  )
})
